import { useState, useRef } from 'react';
import { Container, Button, Dropdown } from '../ui';
import { Menu, X } from 'lucide-react';
import { usePerformanceSentinel } from '../../hooks/usePerformanceSentinel';
import { useVisibilitySentinel } from '../../hooks/useVisibilitySentinel';

/**
 * Header component with sticky navigation and mobile-responsive menu.
 * Contains logo and navigation links.
 */
export function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const headerRef = useRef<HTMLElement>(null);
    const { isPanic } = usePerformanceSentinel();
    const { isVisible } = useVisibilitySentinel(headerRef);
    const shouldAnimate = isVisible && !isPanic;

    const navItems = [
        {
            label: 'Solutions',
            items: [
                { label: 'For visionaries', href: '/visionaries', description: 'The story of AI real-world tools' },
                { label: 'For individuals', href: '/individuals', description: 'Personal AI extensions & ease of use' },
                { label: 'For professionals', href: '/professionals', description: 'AI senior colleague for work' },
                { label: 'For developers', href: '/developers', description: 'MCPaS, licensing & distribution' },
                { label: 'For MCP developers', href: '/mcp-developers', description: 'Build, deploy & monetize' },
                { label: 'For companies', href: '/companies', description: 'Security, SSO & team management' },
                { label: 'For businesses', href: '/businesses', description: 'Overview for everyone' },
            ]
        },
        {
            label: 'Products',
            items: [
                { label: 'About', href: '/about', description: 'Platform overview' },
                { label: 'Why MCP Cloud', href: '/why-mcp-cloud', description: 'What you get with MCP Cloud' },
                { label: 'MCP management', href: '/mcp-management', description: 'Manage your entire MCP ecosystem' },
                { label: 'MCP hosting', href: '/mcp-hosting', description: 'Host MCP servers in isolated cloud environments' },
                { label: 'AI agent hosting', href: '/ai-agent-hosting', description: 'Managed autonomous agents' },
                { label: 'MCP registry', href: '/mcp-registry', description: 'Explore community and private MCP servers' },
                { label: 'MCP Web UI', href: '/mcp-web-ui', description: 'Unified server management console' },
                { label: 'MCP security', href: '/mcp-security', description: 'Unified AI security' },
                { label: 'MCP governance', href: '/mcp-governance', description: 'Scale AI with guardrails' },
                { label: 'MCP observability', href: '/mcp-observability', description: 'Real-time debugging & logs' },
                { label: 'MCP marketplace', href: '/mcp-market', description: 'Lease, use on-demand, or monetize MCP servers' },
            ]
        },
        {
            label: 'Features',
            items: [
                { label: 'Platform features', href: '/features', description: 'Platform capabilities' },
                { label: 'MCP infrastructure', href: '/mcp-infrastructure', description: 'Managed servers, scaling & isolation' },
                { label: 'MCP authentication', href: '/mcp-authentication', description: 'Managed auth & identity' },
                { label: 'Multi-MCP server', href: '/multi-mcp', description: 'Shared filesystem for multiple servers' },
                { label: 'MCP toolsets', href: '/mcp-toolsets', description: 'Curated tool collections for AI agents' },
                { label: 'MCP scanner', href: '/mcp-security-scanner', description: 'Advanced security scanning' },
                { label: 'MCP guardrails', href: '/mcp-guardrails', description: 'Enterprise AI safeguards' },
                { label: 'Security guards', href: '/security-guards', description: 'Secure by design' },
                { label: 'MCP proxy', href: '/mcp-proxy', description: 'Secure runtime bridge' },
                { label: 'API to MCP', href: '/mcp-api-bridge', description: 'Transform OpenAPI to MCP' },
                { label: 'MCP gateway', href: '/mcp-gateway', description: 'Managed control plane' },
                { label: 'MCP payments', href: '/mcp-payments', description: 'Monetize your MCP tools' },
            ]
        },
        {
            label: 'Resources',
            items: [
                { label: 'Blog', href: '/blog', description: 'News, articles, and insights' },
                { label: 'FAQ', href: '/faq', description: 'Frequently asked questions' },
                { label: 'MCP Docs', href: 'https://modelcontextprotocol.io/docs/getting-started/intro', description: 'Official MCP guide' },
                { label: 'Official MCP Registry', href: 'https://registry.modelcontextprotocol.io/', description: 'Discover MCP servers' },
            ]
        }
    ];

    return (
        <>
        <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 z-[200] px-4 py-2 bg-violet-600 text-white font-bold rounded-lg shadow-xl">
            Skip to content
        </a>
        <header ref={headerRef} className="sticky top-0 z-[100]">
            {/* Glass Background - Separate to avoid containing block trap for fixed elements */}
            <div className="absolute inset-x-0 top-0 h-16 backdrop-blur-[10px] saturate-[1.25] bg-black/20 border-b border-white/10" />

            <Container className="h-16 flex items-center justify-between relative z-10">
                <a href="/" className="flex items-center gap-2 font-bold shrink-0 relative z-[110]">
                    <img
                        src="/favicon.svg"
                        alt="MCP Cloud"
                        className="w-8 h-8"
                        aria-hidden="true"
                    />
                    <span className="text-white">MCP Cloud</span>
                </a>

                {/* Desktop Navigation */}
                <nav className="hidden lg:flex items-center gap-2">
                    <Dropdown label="Solutions" items={navItems[0].items} columns={2} />
                    <Dropdown label="Products" items={navItems[1].items} columns={2} align="center" />
                    <Dropdown label="Features" items={navItems[2].items} columns={2} align="right" />
                    <Dropdown label="Resources" items={navItems[3].items} align="right" />
                    <a
                        href="/services"
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors rounded-lg text-slate-300 hover:text-white hover:bg-white/5"
                    >
                        Services
                    </a>
                    <a
                        href="/pricing"
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors rounded-lg text-slate-300 hover:text-white hover:bg-white/5"
                    >
                        Pricing
                    </a>
                    <Button variant="primary" size="md" href="https://app.mcp-cloud.io/?p=log-in">
                        Log In
                    </Button>
                </nav>

                <div className="lg:hidden relative z-[110] flex items-center gap-2">
                    <Button
                        variant="primary"
                        size="sm"
                        href="https://app.mcp-cloud.io/?p=log-in"
                        className="min-h-9 px-4 whitespace-nowrap"
                    >
                        Log In
                    </Button>

                    {/* Mobile Hamburger Button */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="p-2 -mr-2 transition-colors focus:outline-none"
                        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                    >
                        {isMenuOpen ? (
                            <X className="w-6 h-6 text-orange-500" />
                        ) : (
                            <Menu className="w-6 h-6 text-slate-300 hover:text-white" />
                        )}
                    </button>
                </div>
            </Container>

            {/* Mobile Menu Overlay */}
            {isMenuOpen && (
                <div
                    className={`lg:hidden fixed inset-0 z-[90] bg-black/95 backdrop-blur-xl ${shouldAnimate ? 'animate-in fade-in duration-300' : ''} overflow-y-auto`}
                    style={{ paddingTop: '64px' }} // Height of header
                >
                    {/* Close Button */}
                    <button
                        onClick={() => setIsMenuOpen(false)}
                        className="absolute top-4 right-4 p-2 z-[100] transition-colors focus:outline-none"
                        aria-label="Close menu"
                    >
                        <X className="w-6 h-6 text-orange-500 hover:text-orange-400" />
                    </button>

                    <Container className="py-8 flex flex-col gap-8">
                        {/* Home Link */}
                        <a
                            href="/"
                            className="px-4 py-3 rounded-2xl hover:bg-white/5 transition-all duration-200 group active:scale-[0.98]"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <p className="font-bold text-white group-hover:text-orange-400 transition-colors">Home</p>
                        </a>

                        {/* Pricing Link */}
                        <a
                            href="/pricing"
                            className="px-4 py-3 rounded-2xl hover:bg-white/5 transition-all duration-200 group active:scale-[0.98]"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <p className="font-bold text-white group-hover:text-orange-400 transition-colors">Pricing</p>
                        </a>

                        <div className="space-y-4">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-500 px-4">Resources</p>
                            <div className="grid grid-cols-1 gap-1">
                                {navItems[3].items.map((item) => (
                                    <a
                                        key={item.href}
                                        href={item.href}
                                        className="px-4 py-3 rounded-2xl hover:bg-white/5 transition-all duration-200 group active:scale-[0.98]"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <p className="font-bold text-white group-hover:text-orange-400 transition-colors">{item.label}</p>
                                        <p className="text-sm text-slate-400 group-hover:text-slate-300 line-clamp-1">{item.description}</p>
                                    </a>
                                ))}
                            </div>
                        </div>

                        {/* Services Link */}
                        <a
                            href="/services"
                            className="px-4 py-3 rounded-2xl hover:bg-white/5 transition-all duration-200 group active:scale-[0.98]"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            <p className="font-bold text-white group-hover:text-orange-400 transition-colors">Services</p>
                        </a>

                        <div className="space-y-4">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-500 px-4">Solutions</p>
                            <div className="grid grid-cols-1 gap-1">
                                {navItems[0].items.map((item) => (
                                    <a
                                        key={item.href}
                                        href={item.href}
                                        className="px-4 py-3 rounded-2xl hover:bg-white/5 transition-all duration-200 group active:scale-[0.98]"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <p className="font-bold text-white group-hover:text-orange-400 transition-colors">{item.label}</p>
                                        <p className="text-sm text-slate-400 group-hover:text-slate-300 line-clamp-1">{item.description}</p>
                                    </a>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-500 px-4">Product</p>
                            <div className="grid grid-cols-1 gap-1">
                                {navItems[1].items.map((item) => (
                                    <a
                                        key={item.href}
                                        href={item.href}
                                        className="px-4 py-3 rounded-2xl hover:bg-white/5 transition-all duration-200 group active:scale-[0.98]"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <p className="font-bold text-white group-hover:text-orange-400 transition-colors">{item.label}</p>
                                        <p className="text-sm text-slate-400 group-hover:text-slate-300 line-clamp-1">{item.description}</p>
                                    </a>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-orange-500 px-4">Features</p>
                            <div className="grid grid-cols-1 gap-1">
                                {navItems[2].items.map((item) => (
                                    <a
                                        key={item.href}
                                        href={item.href}
                                        className="px-4 py-3 rounded-2xl hover:bg-white/5 transition-all duration-200 group active:scale-[0.98]"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <p className="font-bold text-white group-hover:text-orange-400 transition-colors">{item.label}</p>
                                        <p className="text-sm text-slate-400 group-hover:text-slate-300 line-clamp-1">{item.description}</p>
                                    </a>
                                ))}
                            </div>
                        </div>

                        <div className="mt-4 flex flex-col gap-4">
                            <Button
                                variant="primary"
                                size="lg"
                                href="https://app.mcp-cloud.io/?p=log-in"
                                className="w-full h-14 text-lg bg-orange-500 hover:bg-orange-600 shadow-xl shadow-orange-500/20"
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Log In
                            </Button>
                        </div>
                    </Container>
                </div>
            )}
        </header>
        </>
    );
}
